import { memo, type ReactNode, type RefObject, useEffect } from 'react';

import type { ReplotChartNames } from '@revelio/replots';

import type { PrimaryFilterId } from '../../../filters/types/filters';
import { usePageConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { ChartType } from '../../PageConfigProvider/types/chart';
import type { SecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import type { MetricValues } from '../../PageConfigProvider/types/metric';
import { getFileName } from '../utils/getFileName';
import { ChartActionMenu, type ChartActionMenuProps } from './ChartActionMenu';
import { useChartMenuContext } from './ChartMenuContext';
import { getFormattedNames } from './data-download/converters/utils';
import { usePageExport } from './PageExportProvider';

type ChartActionMenuWrapperProps = {
  chartId: string;
  title: string;
  primaryFilterId?: PrimaryFilterId;
  secondaryDimension?: SecondaryDimensionValues;
  metric?: MetricValues;
  plotChartType?: ChartType;
  slim?: boolean;
  menu?: ChartActionMenuProps['menu'];
  printRef?: RefObject<HTMLDivElement>;
  expandModalContent?: ReactNode;
  data?: unknown;
  dataType?: string;
};

export const ChartActionMenuWrapper = memo(
  ({
    chartId,
    title,
    primaryFilterId,
    secondaryDimension,
    metric,
    plotChartType,
    slim,
    menu,
    printRef,
    expandModalContent,
    data: _data,
    dataType: _dataType,
  }: ChartActionMenuWrapperProps) => {
    const { downloadState, shareState } = useChartMenuContext();
    const { dataProviderSet, customData, customDataType } = downloadState;

    const data = _data || dataProviderSet || customData;
    const dataType =
      _dataType || dataProviderSet?.type || (customDataType ?? undefined);

    const { config: pageConfig } = usePageConfig();

    const { registerChart, unregisterChart } = usePageExport();

    const hasData = data && dataType;

    const shouldRender =
      hasData &&
      menu &&
      // biome-ignore lint/complexity/useIndexOf: migrated from develop
      menu.findIndex((item) => item === 'download-data') !== -1;

    const defaultFileName = getFileName({
      primaryFilterId,
      pageName: pageConfig.name,
      chartTitle: title,
    });

    // Register / unregister chart for PPT export
    useEffect(() => {
      if (hasData) {
        registerChart(chartId, {
          title,
          defaultFileName,
          chartType: shareState?.chartType as ReplotChartNames | undefined,
          plotChartType,
          selectedSegments: shareState?.chartProps?.selectedSegments as
            | { name: string }[]
            | undefined,
          data,
          dataType,
          columnLabels: getFormattedNames(
            primaryFilterId,
            secondaryDimension,
            metric
          ),
        });
        return () => unregisterChart(chartId);
      }
      return undefined;
    }, [
      chartId,
      title,
      defaultFileName,
      data,
      dataType,
      plotChartType,
      shareState,
      hasData,
      registerChart,
      unregisterChart,
      primaryFilterId,
      secondaryDimension,
      metric,
    ]);

    if (!shouldRender) {
      return null;
    }

    return (
      <ChartActionMenu
        menu={menu}
        title={title}
        dataType={dataType}
        data={data}
        shareState={shareState}
        primaryFilterId={primaryFilterId}
        secondaryDimension={secondaryDimension}
        metric={metric}
        plotChartType={plotChartType}
        pageName={pageConfig.name}
        slim={slim}
        printRef={printRef}
        expandModalContent={expandModalContent}
      />
    );
  }
);
